import { api } from '../api';

/**
 * Kunde und Schülerprofil — die Begriffe aus der Kundenbeschreibung.
 *
 * Ein **Kunde** ist der Kontoinhaber: er trägt Rechnungsanschrift und Firma und
 * liegt im Profil mit Index 1. Ein **Schülerprofil** hängt mit Index > 1 daran.
 * Nimmt der Kunde selbst Unterricht, gibt es kein zweites Profil.
 */

export type Kontaktweg = {
  type: string;
  value: string;
  isPrimary?: boolean;
  officeContactAllowed?: boolean;
};

export type StrukturierteAdresse = {
  street?: string;
  houseNumber?: string;
  zip?: string;
  postalCode?: string;
  city?: string;
  country?: string;
  formatted?: string;
};

export type KundenDaten = {
  salutation: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  birthDate?: string;
  isCompany: boolean;
  companyName: string;
  structuredAddress: StrukturierteAdresse;
  communications?: Kontaktweg[];
};

export type SchuelerDaten = {
  salutation: string;
  firstName: string;
  lastName: string;
  birthDate: string;
  /** Kontaktwege des Kontos gelten mit — dann braucht das Profil keine eigenen. */
  kontaktUebernehmen: boolean;
  communications?: Kontaktweg[];
  relationshipType?: string;
  relationshipCustom?: string;
};

export type StammdatenRegeln = {
  kunde: string[];
  schueler: string[];
  anreden: string[];
  landSichtbar: boolean;
};

export type KundenTreffer = {
  personId: number;
  name: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  salutation: string | null;
  birthDate: string | null;
  structuredAddress: StrukturierteAdresse | null;
  isCompany: boolean;
  companyName: string | null;
  communications: Kontaktweg[];
  /** Worüber der Treffer zustande kam: 'email' oder 'telefon'. */
  trefferAuf: string[];
};

export type ProfilZeile = {
  id: string;
  index: number;
  name: string | null;
  firstName: string | null;
  lastName: string | null;
  birthDate: string | null;
  /** Profil 1 — der Kontoinhaber selbst („Mein Profil"). */
  istKonto: boolean;
};

export type KundenAntwort = {
  kunde: KundenTreffer | null;
  profile: ProfilZeile[];
};

export const LEERE_KUNDENDATEN: KundenDaten = {
  salutation: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  birthDate: '',
  isCompany: false,
  companyName: '',
  structuredAddress: {},
};

export const LEERE_SCHUELERDATEN: SchuelerDaten = {
  salutation: '',
  firstName: '',
  lastName: '',
  birthDate: '',
  kontaktUebernehmen: true,
};

export const KONTAKT = 'E-Mail oder Telefonnummer';

/** Fällt der Server aus, gelten die Regeln, die er sonst liefert. */
export const STANDARD_REGELN: StammdatenRegeln = {
  // Für die Probestunde reicht E-Mail oder Telefon; die Anschrift wird erst bei
  // der Anmeldung Pflicht (T-0165).
  kunde: ['Anrede', 'Vorname', 'Nachname', KONTAKT],
  schueler: ['Anrede', 'Vorname', 'Nachname', 'Geburtsdatum', KONTAKT],
  anreden: ['Herr', 'Frau', 'Divers'],
  landSichtbar: false,
};

export const stammdatenApi = {
  regeln: async (standortId?: number | null): Promise<StammdatenRegeln> => {
    const params = new URLSearchParams();
    if (standortId) params.set('standortId', String(standortId));
    const suffix = params.toString() ? `?${params.toString()}` : '';
    return api.get<StammdatenRegeln>(`/api/stammdaten/regeln${suffix}`);
  },
  /**
   * Kunde und Schülerprofil festschreiben und die Profil-Kennung holen.
   *
   * Probestunde anlegen und Anfrage umwandeln brauchen beide einen Teilnehmer;
   * vorher legte jeder Weg seinen eigenen Datensatz an.
   */
  sichern: async (auftrag: {
    personId?: number | null;
    kunde?: KundenDaten;
    schueler?: SchuelerDaten;
    profilWahl: 'konto' | 'bestehend' | 'neu';
    subprofileId?: string;
    standortId?: number | null;
  }): Promise<{ personId: number; subprofileId: string; name: string }> =>
    api.post('/api/kunden/sichern', auftrag),
  kundeZuKontakt: async (
    kontakt: { email?: string; phone?: string; personId?: number | null }
  ): Promise<KundenAntwort> => {
    const params = new URLSearchParams();
    if (kontakt.personId) params.set('personId', String(kontakt.personId));
    if (kontakt.email) params.set('email', kontakt.email);
    if (kontakt.phone) params.set('phone', kontakt.phone);
    if (!params.toString()) return { kunde: null, profile: [] };
    return api.get<KundenAntwort>(`/api/kunden/zu-kontakt?${params.toString()}`);
  },
};

const hatZiffer = (wert: string | undefined | null): boolean => /\d/.test(String(wert || ''));

const TELEFONARTEN = new Set(['mobil', 'festnetz', 'phone', 'telefon', 'tel', 'mobile', 'sms']);

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** Die erste Telefonnummer unter den Kontaktwegen — für Suche und ``personen.phone``. */
export const haupttelefon = (wege: Kontaktweg[] | undefined): string =>
  (wege || []).find((weg) => TELEFONARTEN.has(String(weg.type || '').toLowerCase()) && String(weg.value || '').trim())
    ?.value?.trim() || '';

/**
 * Die Kontaktwege des Kunden fürs Formular. Kam nur eine Nummer mit (aus der
 * Anfrage, aus einem älteren Datensatz), wird sie zum ersten Kontaktweg.
 */
export const kundenKontaktwege = (kunde: { communications?: Kontaktweg[]; phone?: string }): Kontaktweg[] => {
  if (kunde.communications && kunde.communications.length > 0) return kunde.communications;
  const nummer = String(kunde.phone || '').trim();
  return nummer ? [{ type: 'mobil', value: nummer, officeContactAllowed: true }] : [];
};

const emailVorhanden = (wege: Kontaktweg[] | undefined, ...ersatz: (string | undefined | null)[]): boolean => {
  const kandidaten = [
    ...ersatz,
    ...(wege || [])
      .filter((weg) => ['email', 'e-mail'].includes(String(weg.type || '').toLowerCase()))
      .map((weg) => weg.value),
  ];
  return kandidaten.some((wert) => EMAIL.test(String(wert || '').trim()));
};

const telefonVorhanden = (wege: Kontaktweg[] | undefined, ersatz?: string): boolean => {
  const eigene = (wege || []).some(
    (weg) => TELEFONARTEN.has(String(weg.type || '').toLowerCase()) && hatZiffer(weg.value)
  );
  return eigene || hatZiffer(ersatz);
};

/**
 * Dieselbe Prüfung wie im Backend, damit das Formular die Lücke markieren kann,
 * bevor jemand auf Speichern drückt. Die verbindliche Prüfung bleibt der Server —
 * dort steht sie einmal, hier nur als Vorwarnung.
 */
export const fehlendeKundenfelder = (daten: KundenDaten): string[] => {
  const erreichbar =
    telefonVorhanden(daten.communications, daten.phone) || emailVorhanden(daten.communications, daten.email);
  const vorhanden: Record<string, string> = {
    Anrede: daten.salutation?.trim() || '',
    Vorname: daten.firstName?.trim() || '',
    Nachname: daten.lastName?.trim() || '',
    [KONTAKT]: erreichbar ? 'ja' : '',
  };
  const fehlend = STANDARD_REGELN.kunde.filter((feld) => !vorhanden[feld]);
  if (daten.isCompany && !daten.companyName?.trim()) fehlend.unshift('Firmenname');
  return fehlend;
};

export const fehlendeSchuelerfelder = (
  daten: SchuelerDaten,
  kontoTelefon?: string,
  kontoEmail?: string
): string[] => {
  const erreichbar =
    telefonVorhanden(daten.communications, daten.kontaktUebernehmen ? kontoTelefon : undefined)
    || emailVorhanden(daten.communications, daten.kontaktUebernehmen ? kontoEmail : undefined);
  const vorhanden: Record<string, string> = {
    Anrede: daten.salutation?.trim() || '',
    Vorname: daten.firstName?.trim() || '',
    Nachname: daten.lastName?.trim() || '',
    Geburtsdatum: daten.birthDate?.trim() || '',
    [KONTAKT]: erreichbar ? 'ja' : '',
  };
  return STANDARD_REGELN.schueler.filter((feld) => !vorhanden[feld]);
};

/**
 * Was die Anmeldung zusätzlich verlangt: die Anschrift des Kunden (T-0165).
 * Dieselben Namen wie ``fehlende_anmeldefelder`` im Backend.
 */
export const fehlendeAnmeldefelder = (adresse: StrukturierteAdresse | null | undefined): string[] => {
  const vorhanden: Record<string, string> = {
    Straße: String(adresse?.street || '').trim(),
    PLZ: String(adresse?.zip || adresse?.postalCode || '').trim(),
    Ort: String(adresse?.city || '').trim(),
  };
  return ['Straße', 'PLZ', 'Ort'].filter((feld) => !vorhanden[feld]);
};

/** Fehlende Felder als ein Satz — dieselbe Form wie die Meldung des Servers. */
export const fehlendeFelderSatz = (fehlend: string[]): string => {
  if (fehlend.length === 0) return '';
  if (fehlend.length === 1) return `Es fehlt noch: ${fehlend[0]}.`;
  return `Es fehlen noch: ${fehlend.slice(0, -1).join(', ')} und ${fehlend[fehlend.length - 1]}.`;
};

/** Aus einem Kundentreffer das Formular vorbelegen. */
export const kundeAlsFormular = (treffer: KundenTreffer): KundenDaten => ({
  salutation: treffer.salutation || '',
  firstName: treffer.firstName || '',
  lastName: treffer.lastName || '',
  email: treffer.email || '',
  phone: treffer.phone || '',
  birthDate: treffer.birthDate || '',
  isCompany: Boolean(treffer.isCompany),
  companyName: treffer.companyName || '',
  structuredAddress: treffer.structuredAddress || {},
  communications: treffer.communications || [],
});

export type ProfilWahl =
  /** Der Kunde lernt selbst — Profil 1. */
  | { art: 'konto' }
  /** Ein bestehendes Schülerprofil des Kunden. */
  | { art: 'bestehend'; subprofileId: string }
  /**
   * Ein neues Schülerprofil.
   *
   * ``bestaetigt`` heisst: Die Angaben stehen, das Formular ist zu und das
   * Profil liegt als Kachel neben den bestehenden. Für den Auftrag an den Server
   * macht das keinen Unterschied — er bekommt weiterhin ``schueler`` mitgeliefert.
   */
  | { art: 'neu'; bestaetigt?: boolean };

/** Was die Auswahl „Kunde und Schülerprofil" an einer Stelle festhält. */
export type KundeUndProfilWert = {
  /** Gesetzt, wenn ein bestehender Kunde übernommen wurde. */
  personId: number | null;
  kunde: KundenDaten;
  profilWahl: ProfilWahl;
  schueler: SchuelerDaten;
  /** Die Profile des gewählten Kunden — der Aufrufer baut daraus seinen Auftrag. */
  profile: ProfilZeile[];
  /**
   * Das neue Profil ist „hinzugefügt“ und bleibt als Kachel stehen — auch wenn
   * der Bediener danach eine andere Kachel anklickt (T-0044). Vorher hing die
   * Kachel an der Auswahl und verschwand mit ihr.
   */
  entwurfFest?: boolean;
};

export const LEERER_WERT: KundeUndProfilWert = {
  personId: null,
  kunde: LEERE_KUNDENDATEN,
  profilWahl: { art: 'konto' },
  schueler: LEERE_SCHUELERDATEN,
  profile: [],
};

/** Die Kennung des Profils, das gerade gewählt ist — leer bei „neues Profil". */
export const gewaehlteProfilId = (wert: KundeUndProfilWert): string => {
  if (wert.profilWahl.art === 'bestehend') return wert.profilWahl.subprofileId;
  if (wert.profilWahl.art === 'konto') return wert.profile.find((p) => p.istKonto)?.id || '';
  return '';
};
